import { ApiService } from './api.service';
import { ECHO_URL } from '../common/common';

export interface LiveTrackingState {
    showMap: boolean;
    hasTrackingData: boolean;
    isLoading: boolean;
    error: string | null;
}

export interface LiveTrackingCallbacks {
    onFirstDataReceived: () => void;
    onError?: (error: string) => void;
    onLoadingChange?: (isLoading: boolean) => void;
}

export class LiveTrackingService {
    private apiService: ApiService;

    constructor() {
        this.apiService = new ApiService();
    }

    /**
     * Start live tracking for a vehicle
     * @param vehicleRegNo - Vehicle registration number
     * @param callbacks - Callback functions for state updates
     * @returns Promise<boolean> - Success status
     */
    async startLiveTracking(
        vehicleRegNo: string,
        callbacks: LiveTrackingCallbacks
    ): Promise<boolean> {
        try {
            console.log(`🚗 Starting live tracking for vehicle: ${vehicleRegNo}`);

            // Use getApiWithHeadersNoToast to avoid toaster notifications
            const response = await this.apiService.getApiWithHeadersNoToast(
                `https://api.motsop.com/api/tracking/track-vehicle-by-number`,
                {
                    'userid': localStorage.getItem('userId') || 'default-user'
                },
                {
                    vehicleNumber: vehicleRegNo
                }
            );

            if (response?.status === "200" || (response as any)?.success === true) {
                console.log("✅ Live tracking started:", response);
                callbacks.onLoadingChange?.(false);
                return true;
            } else {
                const errorMsg = "Failed to start live tracking";
                console.error("❌ Error starting live tracking:", response);
                callbacks.onError?.(errorMsg);
                return false;
            }
        } catch (error) {
            const errorMsg = `Error starting live tracking: ${error}`;
            console.error("❌ Error starting live tracking:", error);
            callbacks.onError?.(errorMsg);
            return false;
        }
    }

    /**
     * Get initial live tracking state
     */
    getInitialState(): LiveTrackingState {
        return {
            showMap: false,
            hasTrackingData: false,
            isLoading: false,
            error: null
        };
    }

    /**
     * Update state when starting tracking
     */
    getLoadingState(): Partial<LiveTrackingState> {
        return {
            isLoading: true,
            error: null
        };
    }

    /**
     * Update state when tracking starts successfully
     */
    getTrackingStartedState(): Partial<LiveTrackingState> {
        return {
            showMap: true,
            isLoading: false,
            error: null
        };
    }

    /**
     * Update state when first data is received
     */
    getDataReceivedState(): Partial<LiveTrackingState> {
        return {
            hasTrackingData: true,
            isLoading: false,
            error: null
        };
    }

    /**
     * Update state when error occurs
     */
    getErrorState(error: string): Partial<LiveTrackingState> {
        return {
            isLoading: false,
            error,
            showMap: false,
            hasTrackingData: false
        };
    }
}

// Export singleton instance
export const liveTrackingService = new LiveTrackingService();
